import React, { useEffect, useState } from 'react';
import api from '../services/api';

const ROLE_STYLES = {
  referrer:   { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', label: 'Referrer' },
  councillor: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0', label: 'Councillor' },
  admin:      { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', label: 'Admin' },
};

const BLANK = { name: '', email: '', phone: '', password: '', role: 'councillor' };

export default function AdminUsersPage() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [form, setForm]         = useState(BLANK);
  const [creating, setCreating] = useState(false);
  const [formErr, setFormErr]   = useState('');
  const [formOk, setFormOk]     = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/referral/admin/students');
      setUsers(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreate = async e => {
    e.preventDefault(); setFormErr(''); setFormOk('');
    if (!form.name || !form.email || !form.phone || !form.password) {
      setFormErr('All fields are required.'); return;
    }
    setCreating(true);
    try {
      await api.post('/referral/admin/users', form);
      setFormOk(`${form.role === 'admin' ? 'Admin' : 'Councillor'} "${form.name}" created successfully.`);
      setForm(BLANK);
      setShowForm(false);
      load();
    } catch (e) {
      setFormErr(e.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/referral/admin/users/${userId}/role`, { role: newRole });
      setUsers(us => us.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleToggle = async (userId, isActive) => {
    try {
      await api.put(`/referral/admin/students/${userId}/toggle-active`);
      setUsers(us => us.map(u => u.id === userId ? { ...u, is_active: !isActive } : u));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/referral/admin/users/${userId}`);
      setUsers(us => us.filter(u => u.id !== userId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>User Management</h1>
          <p style={S.sub}>{users.length} total account{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button style={S.createBtn} onClick={() => { setShowForm(f => !f); setFormErr(''); setFormOk(''); }}>
          {showForm ? '✕ Cancel' : '+ Create User'}
        </button>
      </div>

      {/* Create User Form */}
      {showForm && (
        <div style={S.formCard}>
          <h2 style={S.formTitle}>New User / Admin Account</h2>
          <form onSubmit={handleCreate} style={S.form}>
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Full Name</label>
                <input name="name" value={form.name} onChange={handleChange} style={S.input} placeholder="Jane Smith" required />
              </div>
              <div style={S.field}>
                <label style={S.label}>Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} style={S.input} placeholder="jane@example.com" required />
              </div>
            </div>
            <div style={S.row}>
              <div style={S.field}>
                <label style={S.label}>Phone (with country code)</label>
                <input name="phone" value={form.phone} onChange={handleChange} style={S.input} placeholder="919876543210" required />
              </div>
              <div style={S.field}>
                <label style={S.label}>Password</label>
                <input name="password" type="password" value={form.password} onChange={handleChange} style={S.input} placeholder="Min 6 characters" required />
              </div>
            </div>
            <div style={{ ...S.field, maxWidth: 220 }}>
              <label style={S.label}>Role</label>
              <select name="role" value={form.role} onChange={handleChange} style={S.input}>
                <option value="councillor">Councillor (view referrals)</option>
                <option value="admin">Admin (full access)</option>
              </select>
            </div>
            {formErr && <div style={S.errBox}>{formErr}</div>}
            {formOk  && <div style={S.okBox}>{formOk}</div>}
            <button type="submit" style={S.submitBtn} disabled={creating}>
              {creating ? 'Creating…' : 'Create Account'}
            </button>
          </form>
        </div>
      )}

      {formOk && !showForm && <div style={S.okBox}>{formOk}</div>}
      {loading && <div style={S.info}>Loading…</div>}
      {error   && <div style={S.errBox}>{error}</div>}

      {!loading && !error && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Name','Email','Phone','Role','Status','Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const rs = ROLE_STYLES[u.role] || ROLE_STYLES.referral;
                return (
                  <tr key={u.id} style={{ ...(i % 2 === 0 ? S.rowEven : S.rowOdd), opacity: u.is_active ? 1 : 0.6 }}>
                    <td style={S.td}>
                      <div style={S.name}>{u.name}</div>
                      <div style={S.sub2}>#{u.id}</div>
                    </td>
                    <td style={S.td}>{u.email}</td>
                    <td style={S.td}>{u.phone}</td>
                    <td style={S.td}>
                      <select
                        value={u.role}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        style={{ ...S.roleSel, background: rs.bg, color: rs.color, border: `1px solid ${rs.border}` }}
                      >
                        <option value="referrer">Referrer</option>
                        <option value="councillor">Councillor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td style={S.td}>
                      <span style={u.is_active ? S.activeB : S.inactiveB}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                        <button
                          style={u.is_active ? S.disableBtn : S.enableBtn}
                          onClick={() => handleToggle(u.id, u.is_active)}
                        >
                          {u.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          style={S.deleteBtn}
                          onClick={() => handleDelete(u.id, u.name)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  page:      { maxWidth: 1100, margin: '0 auto', padding: '2rem 1.25rem', fontFamily: "'Inter','Segoe UI',sans-serif" },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title:     { fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', fontSize: '.88rem', marginTop: '.25rem' },
  createBtn: { padding: '.6rem 1.25rem', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '.9rem', cursor: 'pointer' },
  formCard:  { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,.05)' },
  formTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', marginTop: 0, marginBottom: '1.25rem' },
  form:      {},
  row:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  field:     { display: 'flex', flexDirection: 'column' },
  label:     { fontSize: '.8rem', fontWeight: 600, color: '#374151', marginBottom: '.35rem' },
  input:     { padding: '.65rem .9rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '.9rem', color: '#1e293b', background: '#f8fafc', outline: 'none' },
  errBox:    { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '.7rem .9rem', fontSize: '.88rem', marginBottom: '1rem' },
  okBox:     { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '.7rem .9rem', fontSize: '.88rem', marginBottom: '1rem' },
  submitBtn: { padding: '.75rem 1.75rem', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '.95rem', cursor: 'pointer', marginTop: '.5rem' },
  info:      { textAlign: 'center', color: '#64748b', padding: '2rem' },
  tableWrap: { overflowX: 'auto', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,.05)' },
  table:     { width: '100%', borderCollapse: 'collapse', background: '#fff' },
  th:        { padding: '.75rem 1rem', textAlign: 'left', fontSize: '.78rem', fontWeight: 700, color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '.05em' },
  td:        { padding: '.75rem 1rem', fontSize: '.88rem', color: '#1e293b', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' },
  rowEven:   { background: '#fff' },
  rowOdd:    { background: '#fafafa' },
  name:      { fontWeight: 600, color: '#0f172a' },
  sub2:      { fontSize: '.78rem', color: '#94a3b8', marginTop: '.1rem' },
  roleSel:   { padding: '.25rem .6rem', borderRadius: 6, fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' },
  activeB:   { display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 700, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' },
  inactiveB: { display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
  disableBtn:{ padding: '.3rem .8rem', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: '.8rem', fontWeight: 600, cursor: 'pointer' },
  enableBtn:  { padding: '.3rem .8rem', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: '.8rem', fontWeight: 600, cursor: 'pointer' },
  deleteBtn:  { padding: '.3rem .8rem', background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 6, fontSize: '.8rem', fontWeight: 600, cursor: 'pointer' },
};
